/* © CNES */
import { isDate } from 'moment';
import { Calendar } from 'primereact/calendar';
import { useMemo, useRef } from 'react';
import './CustomCalendar.scss';

/**
 * Custom Calendar Props.
 */
export interface CustomCalendarProps {
  dateValue?: string | Date | null;
  onChange: (value: Date, key: string) => void;
  name: string;
  /** GDH-4215: Minimum selectable date */
  minDate?: Date | null;
  /** GDH-4215: Maximum selectable date */
  maxDate?: Date | null;
}

// Helper to check if a value is considered "has value"
const checkHasValue = (val: string | Date | null | undefined): boolean => {
  return val instanceof Date || (typeof val === 'string' && val.length > 0);
};

/**
 * Custom Calendar.
 * @param dateValue string date displayed in calendar.
 * @param onChange Function handle date change.
 * @param name string id of calendar.
 * @param minDate Minimum selectable date.
 * @param maxDate Maximum selectable date.
 * @returns Element.
 */
export const CustomCalendar = ({
  dateValue,
  onChange,
  name,
  minDate,
  maxDate,
}: CustomCalendarProps) => {
  // GDH-4215: Track reset counter to force Calendar recreation when filter is cleared
  const resetCounterRef = useRef(0);
  const prevDateValueRef = useRef<string | Date | null | undefined>(undefined);
  const isFirstRenderRef = useRef(true);

  // Check if current dateValue has a value
  const hasValue = checkHasValue(dateValue);

  // Synchronous detection of transition from value to null (during render, not in useEffect)
  // This ensures the key is updated BEFORE the Calendar is rendered
  if (!isFirstRenderRef.current) {
    const prevHadValue = checkHasValue(prevDateValueRef.current);
    if (prevHadValue && !hasValue) {
      // Transition detected: value was cleared - increment counter immediately
      resetCounterRef.current += 1;
    }
  }
  isFirstRenderRef.current = false;
  prevDateValueRef.current = dateValue;

  // Convert dateValue to proper format for Calendar
  const calendarValue = useMemo(() => {
    if (!dateValue) return undefined;
    if (dateValue instanceof Date) return dateValue;
    return new Date(dateValue);
  }, [dateValue]);

  // GDH-4215: Create a default date with time set to 00:00:00 for the time picker display
  // Only used when no date is selected
  const defaultDateWithMidnight = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetCounterRef.current]);

  // viewDate determines which month is displayed when the calendar opens
  // - If a date is selected, show that date's month
  // - If no date is selected, show current month with time at 00:00
  const viewDate = useMemo(() => {
    if (calendarValue) {
      return calendarValue;
    }
    return defaultDateWithMidnight;
  }, [calendarValue, defaultDateWithMidnight]);

  const handleChange = (
    value: string | Date | Date[] | undefined | null,
    key: string
  ) => {
    if (isDate(value)) {
      const newDate = new Date(value);

      // GDH-4215: If no previous date exists, set time to 00:00:00
      // This ensures the first selection defaults to midnight instead of current time
      if (!hasValue) {
        // First selection: force time to 00:00:00
        newDate.setHours(0, 0, 0, 0);
      }
      // If previous date exists, preserve the time from calendar selection

      onChange(newDate, key);
    }
  };

  // Generate a key that changes when we need to reset the Calendar
  // This forces React to destroy and recreate the Calendar component, clearing its internal state
  const calendarKey = `${name}-reset-${resetCounterRef.current}`;

  return (
    <Calendar
      key={calendarKey}
      aria-label="calendar-input"
      id={name}
      value={calendarValue}
      viewDate={viewDate}
      dateFormat="dd/mm/yy"
      onChange={(e) => handleChange(e.value, name)}
      showIcon
      showTime
      hideOnDateTimeSelect
      minDate={minDate ?? undefined}
      maxDate={maxDate ?? undefined}
    />
  );
};
